import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { AuthThemeToggle } from '../../components/AuthThemeToggle';
import { Card } from '../../components/Card';
import { Logo } from '../../components/Logo';
import { cn } from '../../lib/cn';

const STEPS = [
  'Account created',
  'Subscription activated',
  'Provisioning your database',
  'Seeding default settings',
  'Preparing your POS',
];

/**
 * Provisioning progress page — §5.5 final box.
 * Walks the checklist from "Account created" → "Preparing your POS" with
 * a simulated delay between each. After done, links to /welcome.
 */
export function SignupProvisioning() {
  const [doneCount, setDoneCount] = useState(0);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    STEPS.forEach((_, idx) => {
      timers.push(
        setTimeout(
          () => setDoneCount((c) => Math.max(c, idx + 1)),
          (idx + 1) * 600,
        ),
      );
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  const allDone = doneCount === STEPS.length;

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark relative flex items-center justify-center px-4 py-12">
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
        <AuthThemeToggle />
      </div>
      <div className="w-full max-w-lg">
        <div className="flex flex-col items-center mb-8">
          <Logo className="w-10 h-10 rounded-xl" />
          <h1 className="mt-4 text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 text-center">
            {allDone ? 'Your POS is ready' : 'Setting up your POS…'}
          </h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300 text-center max-w-md">
            {allDone
              ? 'Your POS is live. Open it any time at the URL below.'
              : "Hang tight — this usually takes about 30 seconds. You don't need to refresh."}
          </p>
        </div>

        <Card className="p-5 sm:p-7">
          <ol className="space-y-3">
            {STEPS.map((label, idx) => {
              const isDone = idx < doneCount;
              const isCurrent = idx === doneCount;
              return (
                <li
                  key={label}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-md transition',
                    isDone && 'bg-emerald-50 dark:bg-emerald-950/30',
                    isCurrent && 'bg-brand-subtle dark:bg-brand-dm-subtle/30',
                  )}
                >
                  <span
                    className={cn(
                      'w-6 h-6 grid place-items-center rounded-full flex-shrink-0',
                      isDone &&
                        'bg-emerald-600 text-white dark:bg-emerald-500 dark:text-zinc-950',
                      isCurrent && 'bg-brand text-white dark:bg-brand-dm dark:text-zinc-950',
                      !isDone &&
                        !isCurrent &&
                        'bg-zinc-200 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-500',
                    )}
                  >
                    {isDone ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : isCurrent ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                    )}
                  </span>
                  <span
                    className={cn(
                      'text-sm font-medium',
                      isDone && 'text-zinc-950 dark:text-zinc-50',
                      isCurrent && 'text-zinc-950 dark:text-zinc-50',
                      !isDone && !isCurrent && 'text-zinc-500 dark:text-zinc-400',
                    )}
                  >
                    {label}
                  </span>
                  {isCurrent && (
                    <span className="ml-auto text-xs text-zinc-500 dark:text-zinc-400 tabular-nums">
                      ~{(STEPS.length - doneCount) * 0.6}s
                    </span>
                  )}
                </li>
              );
            })}
          </ol>

          <div className="mt-6 pt-5 border-t border-zinc-200 dark:border-zinc-700 text-center">
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              We're preparing your dedicated POS workspace.
            </p>
          </div>
        </Card>

        {allDone && (
          <div className="mt-6 flex justify-center">
            <Link
              to="/welcome"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-base font-medium bg-brand text-white shadow-xs hover:bg-brand-hover dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover transition"
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {!allDone && (
          <p className="mt-6 text-center text-xs text-zinc-500 dark:text-zinc-400">
            You can close this tab — we'll email you when it's done.
          </p>
        )}
      </div>
    </div>
  );
}
